import React, { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "tailwindcss/tailwind.css";
import { BASE_URL } from "../../config";
import { ArrowLeftIcon, XMarkIcon, PlusIcon, MagnifyingGlassIcon, PencilSquareIcon } from "@heroicons/react/24/outline";
import { motion } from "framer-motion";
import Select from "react-select";
import { useTable, usePagination } from "react-table";

const EditCpt = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { cptCode } = useParams();
  const isViewMode = location.pathname.startsWith("/cpt/view/");
  const [formData, setFormData] = useState({
    cpt_code: "",
    cpt_desc: "",
    default_price: "",
    cpt_categ_code: "",
  });
  const [cptRates, setCptRates] = useState([]);
  const [rateForm, setRateForm] = useState({
    provider_code: "",
    provider_cpt_code: "",
    cpt_rate: "",
  });
  const [providers, setProviders] = useState([]);
  const [loadingProviders, setLoadingProviders] = useState(true);
  const [loadingCpt, setLoadingCpt] = useState(true);
  const [errors, setErrors] = useState({});
  const [rateSearchQuery, setRateSearchQuery] = useState("");
  const [rateCurrentPage, setRateCurrentPage] = useState(1);
  const [rateItemsPerPage, setRateItemsPerPage] = useState(5);

  const categories = [
    { value: "", label: "None" },
    { value: "Evaluation and Management", label: "Evaluation and Management" },
    { value: "Anesthesiology", label: "Anesthesiology" },
    { value: "Surgery", label: "Surgery" },
    { value: "Radiology", label: "Radiology" },
    { value: "Pathology and Laboratory", label: "Pathology and Laboratory" },
    { value: "Medicine", label: "Medicine" },
  ];

  useEffect(() => {
    const fetchCpt = async () => {
      setLoadingCpt(true);
      try {
        const response = await axios.get(`${BASE_URL}/cpt/${cptCode}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authentication/token")}`,
          },
        });
        const { cpt, cpt_rates } = response.data.data;
        setFormData({
          cpt_code: cpt.cpt_code,
          cpt_desc: cpt.cpt_desc,
          default_price: cpt.default_price,
          cpt_categ_code: cpt.cpt_categ_code || "",
        });
        setCptRates(
          cpt_rates
            .filter((rate) => rate.provider_cpt_code && rate.cpt_rate)
            .map((rate) => ({
              provider_code: rate.provider_code,
              provider_name: rate.provider_name,
              provider_cpt_code: rate.provider_cpt_code,
              cpt_rate: parseFloat(rate.cpt_rate),
            }))
        );
      } catch (error) {
        toast.error("Failed to fetch CPT data.", { autoClose: 3000 });
        setTimeout(() => navigate("/cpt"), 2000);
      } finally {
        setLoadingCpt(false);
      }
    };

    const fetchProviders = async () => {
      setLoadingProviders(true);
      try {
        const response = await axios.get(
          `${BASE_URL}/providers/list/for-dropdown-select`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("authentication/token")}`,
            },
            params: {
              limit: 5000,
            },
          }
        );
        console.log("Providers API response:", response.data);
        const providersData = Array.isArray(response.data.data?.providers)
          ? response.data.data.providers
          : [];
        setProviders(providersData);
      } catch (error) {
        console.error("Failed to fetch providers:", error);
        toast.error("Failed to fetch providers.", { autoClose: 3000 });
        setProviders([]);
      } finally {
        setLoadingProviders(false);
      }
    };

    fetchCpt();
    if (!isViewMode) {
      fetchProviders();
    }
  }, [cptCode, isViewMode, navigate]);

  const providerOptions = useMemo(() => {
    const addedProviderCodes = cptRates.map((rate) => rate.provider_code);
    return providers
      .filter((provider) => !addedProviderCodes.includes(provider.provider_code))
      .map((provider) => ({
        value: provider.provider_code,
        label: `${provider.provider_name} (${provider.provider_code})`,
        provider_name: provider.provider_name,
      }));
  }, [providers, cptRates]);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleRateFormChange = (e) => {
    const { name, value } = e.target;
    setRateForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleProviderSelect = (selected) => {
    setRateForm((prev) => ({
      ...prev,
      provider_code: selected ? selected.value : "",
    }));
    setErrors((prev) => ({ ...prev, provider_code: "" }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.cpt_desc) newErrors.cpt_desc = "Description is required.";
    if (!formData.default_price || formData.default_price <= 0)
      newErrors.default_price = "Default rate must be a positive number.";
    return newErrors;
  };

  const validateRateForm = () => {
    const newErrors = {};
    if (!rateForm.provider_code)
      newErrors.provider_code = "Provider is required.";
    if (!rateForm.provider_cpt_code)
      newErrors.provider_cpt_code = "Provider CPT code is required.";
    if (!rateForm.cpt_rate || rateForm.cpt_rate <= 0)
      newErrors.cpt_rate = "Rate must be a positive number.";
    return newErrors;
  };

  const handleAddRate = () => {
    if (isViewMode) return;

    const rateErrors = validateRateForm();
    if (Object.keys(rateErrors).length > 0) {
      Object.entries(rateErrors).forEach(([key, message]) => {
        toast.error(message, { autoClose: 3000 });
      });
      setErrors(rateErrors);
      return;
    }
    const provider = providers.find(
      (p) => p.provider_code === rateForm.provider_code
    );
    setCptRates((prev) => [
      ...prev,
      {
        provider_code: rateForm.provider_code,
        provider_name: provider ? provider.provider_name : "Unknown",
        provider_cpt_code: rateForm.provider_cpt_code,
        cpt_rate: parseFloat(rateForm.cpt_rate),
      },
    ]);
    setRateForm({ provider_code: "", provider_cpt_code: "", cpt_rate: "" });
    setRateCurrentPage(1);
  };

  const handleRemoveRate = (index) => {
    if (isViewMode) return;

    setCptRates((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isViewMode) return;

    const formErrors = validateForm();
    if (Object.keys(formErrors).length > 0) {
      Object.entries(formErrors).forEach(([key, message]) => {
        toast.error(message, { autoClose: 3000 });
      });
      setErrors(formErrors);
      return;
    }
    if (cptRates.length === 0) {
      toast.error("At least one CPT rate is required.", { autoClose: 3000 });
      return;
    }
    try {
      const response = await axios.put(
        `${BASE_URL}/cpt/${cptCode}`,
        {
          cpt_desc: formData.cpt_desc,
          default_price: parseFloat(formData.default_price),
          cpt_categ_code: formData.cpt_categ_code || null,
          cpt_rates: cptRates,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem(
              "authentication/token"
            )}`,
          },
        }
      );
      toast.success("CPT updated successfully.", { autoClose: 3000 });
      setTimeout(() => navigate("/cpt"), 2000);
    } catch (error) {
      if (error.response?.status === 422 && error.response?.data?.message) {
        if (typeof error.response.data.message === "string") {
          toast.error(error.response.data.message, { autoClose: 3000 });
        } else {
          Object.entries(error.response.data.message).forEach(([key, messages]) => {
            const messageArray = Array.isArray(messages) ? messages : [messages];
            const match = key.match(/^cpt_rates\.(\d+)\.(.+)$/);
            if (match) {
              const index = parseInt(match[1]);
              const providerName = cptRates[index]?.provider_name || "Unknown Provider";
              messageArray.forEach((message) => {
                toast.error(`${providerName}: ${message}`, { autoClose: 3000 });
              });
            } else {
              messageArray.forEach((message) => {
                toast.error(message, { autoClose: 3000 });
              });
            }
          });
        }
      } else {
        toast.error("Failed to update CPT.", { autoClose: 3000 });
      }
    }
  };

  const filteredCptRates = useMemo(() => {
    if (!rateSearchQuery) return cptRates;
    return cptRates.filter((rate) =>
    //   [rate.provider_name, rate.provider_cpt_code, rate.cpt_rate.toString()]
      [rate.provider_name, rate.provider_cpt_code, rate.cpt_rate]
        .join(" ")
        .toLowerCase()
        .includes(rateSearchQuery.toLowerCase())
    );
  }, [cptRates, rateSearchQuery]);

  const columns = useMemo(() => {
    const rateColumns = [
      {
        Header: "Provider Name",
        accessor: "provider_name",
        Cell: ({ value }) => (
          <span className="text-xs text-gray-900">{value}</span>
        ),
      },
      {
        Header: "Provider CPT Code",
        accessor: "provider_cpt_code",
        Cell: ({ value }) => (
          <span className="text-xs text-gray-900">{value}</span>
        ),
      },
      {
        Header: "Rate",
        accessor: "cpt_rate",
        Cell: ({ value }) => (
          <span className="text-xs text-gray-900">
            ₱{parseFloat(value).toLocaleString()}
          </span>
        ),
      },
    ];

    if (!isViewMode) {
      rateColumns.push({
        Header: "Actions",
        accessor: "actions",
        Cell: ({ row }) => (
          <button
            onClick={() => handleRemoveRate(row.index)}
            className="text-red-600 hover:text-red-800"
            title="Remove"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        ),
      });
    }

    return rateColumns;
  }, [isViewMode]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns,
      data: filteredCptRates,
      initialState: { pageIndex: rateCurrentPage - 1, pageSize: rateItemsPerPage },
      manualPagination: false,
    },
    usePagination
  );

  const getPageNumbers = () => {
    const maxPagesToShow = 5;
    const startPage = Math.max(1, pageIndex + 1 - Math.floor(maxPagesToShow / 2));
    const endPage = Math.min(
      pageOptions.length,
      startPage + maxPagesToShow - 1
    );
    return Array.from(
      { length: endPage - startPage + 1 },
      (_, i) => startPage + i
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 py-2 px-2 sm:px-3 lg:px-4">
      <ToastContainer position="top-right" autoClose={3000} />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-1xl mx-auto"
      >
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h1 className="text-xl font-semibold text-gray-900">
            {isViewMode ? "View CPT" : "Edit CPT"}
          </h1>
          <div className="flex flex-wrap items-center gap-2 self-start sm:self-auto">
            {isViewMode && (
              <button
                type="button"
                onClick={() => navigate(`/cpt/edit/${cptCode}`)}
                className="inline-flex items-center justify-center gap-2 rounded-md bg-amber-500 px-3 py-2 text-sm font-medium text-white hover:bg-amber-600"
              >
                <PencilSquareIcon className="h-5 w-5" />
                <span>Edit</span>
              </button>
            )}
            <button
              type="button"
              onClick={() => navigate("/cpt")}
              className="inline-flex items-center justify-center gap-2 rounded-md bg-gray-100 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200"
            >
              <ArrowLeftIcon className="h-5 w-5" />
              <span>Back to CPT Rates</span>
            </button>
          </div>
        </div>
        {loadingCpt ? (
          <div className="text-center py-4">Loading...</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-[35%_65%] gap-4">
            {/* Left Side: CPT Details Form */}
            <div className="bg-white p-4 rounded-lg shadow">
              <h3 className="text-lg font-semibold mb-4">CPT Details</h3>
              <form>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700">
                    CPT Code
                  </label>
                  <input
                    type="text"
                    value={formData.cpt_code}
                    disabled
                    className="mt-1 block w-full border border-gray-300 rounded-md py-2 px-3 bg-gray-100 text-gray-500 text-sm"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700">
                    CPT Description *
                  </label>
                  <textarea
                    rows="4"
                    cols="50"
                    name="cpt_desc"
                    value={formData.cpt_desc}
                    onChange={handleFormChange}
                    disabled={isViewMode}
                    className="mt-1 block w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-600"
                  />
                  {errors.cpt_desc && (
                    <p className="mt-1 text-sm text-red-600">{errors.cpt_desc}</p>
                  )}
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700">
                    Default Rate *
                  </label>
                  <input
                    type="number"
                    name="default_price"
                    value={formData.default_price}
                    onChange={handleFormChange}
                    min="0"
                    step="0.01"
                    disabled={isViewMode}
                    className="mt-1 block w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-600"
                  />
                  {errors.default_price && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.default_price}
                    </p>
                  )}
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700">
                    Category
                  </label>
                  <select
                    name="cpt_categ_code"
                    value={formData.cpt_categ_code}
                    onChange={handleFormChange}
                    disabled={isViewMode}
                    className="mt-1 block w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-600"
                  >
                    {categories.map((cat) => (
                      <option key={cat.value} value={cat.value}>
                        {cat.label}
                      </option>
                    ))}
                  </select>
                </div>
              </form>
            </div>
            {/* Right Side: CPT Rates Form and Table */}
            <div className="bg-white p-4 rounded-lg shadow">
              <h3 className="text-lg font-semibold mb-4">CPT Rates</h3>
              {!isViewMode && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Search Provider *
                      </label>
                      <Select
                        options={providerOptions}
                        value={providerOptions.find(
                          (option) => option.value === rateForm.provider_code
                        )}
                        onChange={handleProviderSelect}
                        placeholder="Search provider..."
                        isClearable
                        isDisabled={loadingProviders}
                        className="mt-1 text-sm"
                        classNamePrefix="select"
                      />
                      {errors.provider_code && (
                        <p className="mt-1 text-sm text-red-600">
                          {errors.provider_code}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Provider CPT Code *
                      </label>
                      <input
                        type="text"
                        name="provider_cpt_code"
                        value={rateForm.provider_cpt_code}
                        onChange={handleRateFormChange}
                        className="mt-1 block w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm"
                      />
                      {errors.provider_cpt_code && (
                        <p className="mt-1 text-sm text-red-600">
                          {errors.provider_cpt_code}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Provider Rate *
                      </label>
                      <input
                        type="number"
                        name="cpt_rate"
                        value={rateForm.cpt_rate}
                        onChange={handleRateFormChange}
                        min="0"
                        step="0.01"
                        className="mt-1 block w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm"
                      />
                      {errors.cpt_rate && (
                        <p className="mt-1 text-sm text-red-600">{errors.cpt_rate}</p>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={handleAddRate}
                    className="flex items-center space-x-1 bg-blue-600 text-white px-3 py-2 rounded-md text-sm hover:bg-blue-700 mb-4"
                  >
                    <PlusIcon className="h-5 w-5" />
                    <span>Add Rate</span>
                  </button>
                </>
              )}
              <div className="mb-4 relative">
                <input
                  type="text"
                  placeholder="Search rates by provider, CPT code, or rate..."
                  value={rateSearchQuery}
                  onChange={(e) => {
                    setRateSearchQuery(e.target.value);
                    setRateCurrentPage(1);
                  }}
                  className="pl-8 pr-4 py-2 border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm w-full"
                />
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-2 top-1/2 transform -translate-y-1/2" />
              </div>
              <div className="overflow-x-auto">
                <table
                  {...getTableProps()}
                  className="min-w-full divide-y divide-gray-200"
                >
                  <thead className="bg-gray-50">
                    {headerGroups.map((headerGroup) => (
                      <tr {...headerGroup.getHeaderGroupProps()}>
                        {headerGroup.headers.map((column) => (
                          <th
                            {...column.getHeaderProps()}
                            className="px-3 py-2 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider"
                          >
                            {column.render("Header")}
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>
                  <tbody
                    {...getTableBodyProps()}
                    className="bg-white divide-y divide-gray-200"
                  >
                    {page.length === 0 ? (
                      <tr>
                        <td
                          colSpan={columns.length}
                          className="px-3 py-2 text-center text-xs text-gray-500"
                        >
                          {rateSearchQuery
                            ? "No matching rates found."
                            : "No rates added."}
                        </td>
                      </tr>
                    ) : (
                      page.map((row) => {
                        prepareRow(row);
                        return (
                          <tr {...row.getRowProps()}>
                            {row.cells.map((cell) => (
                              <td
                                {...cell.getCellProps()}
                                className="px-3 py-2 whitespace-nowrap text-xs text-gray-900"
                              >
                                {cell.render("Cell")}
                              </td>
                            ))}
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
              <div className="mt-2 flex flex-col sm:flex-row justify-between items-center gap-2 px-4 py-2">
                <div className="text-xs text-gray-600">
                  Showing {pageIndex * pageSize + 1} to{" "}
                  {Math.min((pageIndex + 1) * pageSize, filteredCptRates.length)} of{" "}
                  {filteredCptRates.length.toLocaleString()} entries
                </div>
                <div className="flex items-center gap-1 flex-wrap">
                  <button
                    onClick={() => gotoPage(0)}
                    disabled={!canPreviousPage}
                    className={`px-2 py-0.5 rounded-md text-xs ${
                      !canPreviousPage
                        ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    First
                  </button>
                  <button
                    onClick={() => previousPage()}
                    disabled={!canPreviousPage}
                    className={`px-2 py-0.5 rounded-md text-xs ${
                      !canPreviousPage
                        ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    Previous
                  </button>
                  {getPageNumbers().map((pageNum) => (
                    <button
                      key={pageNum}
                      onClick={() => gotoPage(pageNum - 1)}
                      className={`px-2 py-0.5 rounded-md text-xs ${
                        pageIndex + 1 === pageNum
                          ? "bg-blue-600 text-white"
                          : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                      }`}
                    >
                      {pageNum}
                    </button>
                  ))}
                  <button
                    onClick={() => nextPage()}
                    disabled={!canNextPage}
                    className={`px-2 py-0.5 rounded-md text-xs ${
                      !canNextPage
                        ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    Next
                  </button>
                  <button
                    onClick={() => gotoPage(pageOptions.length - 1)}
                    disabled={!canNextPage}
                    className={`px-2 py-0.5 rounded-md text-xs ${
                      !canNextPage
                        ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    Last
                  </button>
                  <select
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      setRateCurrentPage(1);
                    }}
                    className="border border-gray-200 rounded-md px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                  >
                    {[5, 10, 20, 50].map((size) => (
                      <option key={size} value={size}>
                        {size} per page
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              {!isViewMode && (
                <div className="mt-6 flex justify-end">
                  <button
                    onClick={handleSubmit}
                    className="bg-green-600 text-white px-4 py-2 rounded-md text-sm hover:bg-green-700"
                  >
                    Save
                  </button>
                </div>
              )}
            </div>
            
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default EditCpt;
